import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Users, Plus, Trash2, Edit, Calendar, CheckCircle, XCircle, Music, Video, Baby, Handshake, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import moment from 'moment';

export default function AdminLideres() {
  const [showDialog, setShowDialog] = useState(false);
  const [editingLeader, setEditingLeader] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [searchMember, setSearchMember] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    members: [],
    description: '',
    start_date: '',
    is_active: true
  });

  const queryClient = useQueryClient();

  const { data: leaders = [], isLoading } = useQuery({
    queryKey: ['leaders'],
    queryFn: () => base44.entities.Leader.list('-created_date')
  });

  const { data: members = [] } = useQuery({
    queryKey: ['members-search', searchMember],
    queryFn: async () => {
      const activeMembers = await base44.entities.Member.filter({ status: 'Ativo' }, '-created_date', 1000);
      const term = searchMember.trim().toLowerCase();
      return activeMembers.filter((m) => {
        const name = (m.full_name || '').toLowerCase();
        const email = (m.email || '').toLowerCase();
        return name.includes(term) || email.includes(term);
      }).sort((a, b) =>
        String(a.full_name || '').localeCompare(String(b.full_name || ''), 'pt-BR', { sensitivity: 'base' })
      );
    },
    enabled: searchMember.trim().length > 1
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Leader.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaders'] });
      toast.success('Líder adicionado com sucesso!');
      resetForm();
    },
    onError: () => toast.error('Erro ao adicionar líder.')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Leader.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaders'] });
      toast.success('Líder atualizado com sucesso!');
      resetForm();
    },
    onError: () => toast.error('Erro ao atualizar líder.')
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Leader.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaders'] });
      toast.success('Líder removido com sucesso!');
      setDeleteId(null);
    },
    onError: () => toast.error('Erro ao remover líder.')
  });

  const handleSubmit = async () => {
    if (!formData.title) {
      toast.error('Preencha o título da liderança.');
      return;
    }

    try {
      if (editingLeader) {
        await updateMutation.mutateAsync({ id: editingLeader.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }

      // Vincular aos ministérios
      for (const member of formData.members) {
        if (!member.link_to_worship && !member.link_to_media && !member.link_to_dance && !member.link_to_reception && !member.link_to_kids) continue;

        const churchMember = await base44.entities.Member.filter({ id: member.member_id }).then(m => m[0]);
        if (!churchMember) continue;

        // Louvor
        if (member.link_to_worship) {
          const existing = await base44.entities.WorshipTeamMember.filter({ member_id: churchMember.id });
          if (existing.length === 0) {
            await base44.entities.WorshipTeamMember.create({
              full_name: churchMember.full_name,
              phone: churchMember.phone,
              email: churchMember.email,
              voices: member.worship_voices || [],
              instruments: member.worship_instruments || [],
              member_id: churchMember.id,
              member_name: churchMember.full_name
            });
          }
        }

        // Mídia
        if (member.link_to_media) {
          const existing = await base44.entities.MediaTeamMember.filter({ member_id: churchMember.id });
          if (existing.length === 0) {
            await base44.entities.MediaTeamMember.create({
              full_name: churchMember.full_name,
              phone: churchMember.phone,
              email: churchMember.email,
              roles: member.media_roles || [],
              member_id: churchMember.id,
              member_name: churchMember.full_name
            });
          }
        }

        // Dança MBV
        if (member.link_to_dance) {
          const existing = await base44.entities.DanceTeamMember.filter({ member_id: churchMember.id });
          if (existing.length === 0) {
            await base44.entities.DanceTeamMember.create({
              full_name: churchMember.full_name,
              phone: churchMember.phone,
              email: churchMember.email,
              roles: ['Dançar'],
              member_id: churchMember.id,
              member_name: churchMember.full_name
            });
          }
        }

        if (member.link_to_reception) {
          const existing = await base44.entities.ReceptionTeamMember.filter({ member_id: churchMember.id });
          if (existing.length === 0) {
            await base44.entities.ReceptionTeamMember.create({
              full_name: churchMember.full_name,
              phone: churchMember.phone,
              email: churchMember.email,
              roles: member.reception_roles || [],
              member_id: churchMember.id,
              member_name: churchMember.full_name
            });
          }
        }

        // Kids MBV
        if (member.link_to_kids) {
          const existing = await base44.entities.KidsMBVMember.filter({ member_id: churchMember.id });
          if (existing.length === 0) {
            await base44.entities.KidsMBVMember.create({
              full_name: churchMember.full_name,
              phone: churchMember.phone,
              email: churchMember.email,
              roles: member.kids_roles || [],
              member_id: churchMember.id,
              member_name: churchMember.full_name
            });
          }
        }
      }

      toast.success('Vínculos de ministério criados!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao vincular membro aos ministérios.');
    }
  };

  const handleEdit = (leader) => {
    setEditingLeader(leader);
    setFormData({
      title: leader.title || '',
      members: leader.members || [],
      description: leader.description || '',
      start_date: leader.start_date || '',
      is_active: leader.is_active !== false
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      members: [],
      description: '',
      start_date: '',
      is_active: true
    });
    setEditingLeader(null);
    setShowDialog(false);
    setSearchMember('');
  };

  const selectMember = (member) => {
    const alreadyAdded = formData.members.some(m => m.member_id === member.id);
    if (alreadyAdded) {
      toast.info('Este membro já foi adicionado');
      return;
    }
    setFormData({
      ...formData,
      members: [...formData.members, { 
        member_id: member.id, 
        member_name: member.full_name,
        link_to_worship: false,
        worship_voices: [],
        worship_instruments: [],
        link_to_media: false,
        media_roles: [],
        link_to_dance: false,
        link_to_reception: false,
        reception_roles: [],
        link_to_kids: false,
        kids_roles: []
      }]
    });
    setSearchMember('');
  };

  const removeMember = (memberId) => {
    setFormData({
      ...formData,
      members: formData.members.filter(m => m.member_id !== memberId)
    });
  };

  if (isLoading) {
    return <LoadingSpinner text="Carregando líderes..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <PageHeader 
            title="Líderes" 
            subtitle="Gerencie as lideranças da igreja"
            action={
              <Button 
                onClick={() => setShowDialog(true)}
                className="bg-white text-indigo-600 hover:bg-gray-100"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar
              </Button>
            }
          />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-14">
        {leaders.length === 0 ? (
          <EmptyState
            Icon={Users}
            title="Nenhum líder cadastrado"
            description="Adicione os líderes da sua igreja"
            actionLabel="Adicionar Líder"
            onAction={() => setShowDialog(true)}
          />
        ) : (
          <div className="grid gap-4">
            {leaders.map((leader, index) => (
              <motion.div
                key={leader.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{leader.title}</h3>
                      {leader.is_active ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Ativo
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full flex items-center gap-1">
                          <XCircle className="w-3 h-3" />
                          Inativo
                        </span>
                      )}
                    </div>
                    
                    {leader.members && leader.members.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {leader.members.map((member, idx) => (
                          <span key={idx} className="px-3 py-1 bg-indigo-100 text-indigo-700 text-sm rounded-full">
                            {member.member_name}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {leader.description && (
                      <p className="text-gray-600 text-sm mb-3">{leader.description}</p>
                    )}
                    
                    {leader.start_date && (
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <Calendar className="w-4 h-4" />
                        <span>Desde {moment(leader.start_date).format('DD/MM/YYYY')}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(leader)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteId(leader.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Dialog Add/Edit */}
      <Dialog open={showDialog} onOpenChange={(open) => !open && resetForm()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLeader ? 'Editar Líder' : 'Adicionar Líder'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Título da Liderança *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Líder de Louvor, Líder de Mídia, Líder de Recepção..."
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Dica: Use títulos como "Líder de Louvor", "Líder de Mídia", "Líder de Dança", "Líder de Recepção" ou "Líder Kids MBV" para vincular às equipes
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Membros Vinculados</label>
              
              {formData.members.length > 0 && (
                <div className="space-y-3 mb-3">
                  {formData.members.map((member, idx) => (
                    <div key={member.member_id} className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-indigo-900 font-bold">{member.member_name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMember(member.member_id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Remover
                        </Button>
                      </div>

                      <div className="space-y-3 border-t border-indigo-200 pt-3">
                        <p className="text-xs font-semibold text-indigo-700 mb-2">Vincular aos Ministérios:</p>

                        {/* Louvor */}
                        <div className="bg-white rounded-lg p-3 border border-purple-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Checkbox
                              id={`worship-${idx}`}
                              checked={member.link_to_worship}
                              onCheckedChange={(checked) => {
                                const updated = [...formData.members];
                                updated[idx].link_to_worship = checked;
                                setFormData({ ...formData, members: updated });
                              }}
                            />
                            <Label htmlFor={`worship-${idx}`} className="flex items-center gap-2 cursor-pointer">
                              <Music className="w-4 h-4 text-purple-600" />
                              <span className="text-sm font-semibold">Equipe de Louvor</span>
                            </Label>
                          </div>
                          {member.link_to_worship && (
                            <div className="ml-6 space-y-2 text-xs">
                              <div>
                                <Label className="text-xs">Vozes:</Label>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {['Soprano', 'Contralto', 'Tenor', 'Barítono'].map(voice => (
                                    <label key={voice} className="flex items-center gap-1 px-2 py-1 bg-purple-50 rounded">
                                      <input
                                        type="checkbox"
                                        checked={member.worship_voices?.includes(voice)}
                                        onChange={(e) => {
                                          const updated = [...formData.members];
                                          if (e.target.checked) {
                                            updated[idx].worship_voices = [...(member.worship_voices || []), voice];
                                          } else {
                                            updated[idx].worship_voices = member.worship_voices.filter(v => v !== voice);
                                          }
                                          setFormData({ ...formData, members: updated });
                                        }}
                                        className="w-3 h-3"
                                      />
                                      <span className="text-xs">{voice}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs">Instrumentos:</Label>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {['Bateria', 'Baixo', 'Guitarra', 'Violão', 'Teclado'].map(inst => (
                                    <label key={inst} className="flex items-center gap-1 px-2 py-1 bg-purple-50 rounded">
                                      <input
                                        type="checkbox"
                                        checked={member.worship_instruments?.includes(inst)}
                                        onChange={(e) => {
                                          const updated = [...formData.members];
                                          if (e.target.checked) {
                                            updated[idx].worship_instruments = [...(member.worship_instruments || []), inst];
                                          } else {
                                            updated[idx].worship_instruments = member.worship_instruments.filter(i => i !== inst);
                                          }
                                          setFormData({ ...formData, members: updated });
                                        }}
                                        className="w-3 h-3"
                                      />
                                      <span className="text-xs">{inst}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Mídia */}
                        <div className="bg-white rounded-lg p-3 border border-orange-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Checkbox
                              id={`media-${idx}`}
                              checked={member.link_to_media}
                              onCheckedChange={(checked) => {
                                const updated = [...formData.members];
                                updated[idx].link_to_media = checked;
                                setFormData({ ...formData, members: updated });
                              }}
                            />
                            <Label htmlFor={`media-${idx}`} className="flex items-center gap-2 cursor-pointer">
                              <Video className="w-4 h-4 text-orange-600" />
                              <span className="text-sm font-semibold">Equipe de Mídia</span>
                            </Label>
                          </div>
                          {member.link_to_media && (
                            <div className="ml-6 space-y-2 text-xs">
                              <Label className="text-xs">Funções:</Label>
                              <div className="flex flex-wrap gap-1">
                                {['Câmera', 'Edição', 'Transmissão', 'Som', 'Iluminação', 'Projeção'].map(role => (
                                  <label key={role} className="flex items-center gap-1 px-2 py-1 bg-orange-50 rounded">
                                    <input
                                      type="checkbox"
                                      checked={member.media_roles?.includes(role)}
                                      onChange={(e) => {
                                        const updated = [...formData.members];
                                        if (e.target.checked) {
                                          updated[idx].media_roles = [...(member.media_roles || []), role];
                                        } else {
                                          updated[idx].media_roles = member.media_roles.filter(r => r !== role);
                                        }
                                        setFormData({ ...formData, members: updated });
                                      }}
                                      className="w-3 h-3"
                                    />
                                    <span className="text-xs">{role}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Dança MBV */}
                        <div className="bg-white rounded-lg p-3 border border-pink-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Checkbox
                              id={`dance-${idx}`}
                              checked={member.link_to_dance}
                              onCheckedChange={(checked) => {
                                const updated = [...formData.members];
                                updated[idx].link_to_dance = checked;
                                setFormData({ ...formData, members: updated });
                              }}
                            />
                            <Label htmlFor={`dance-${idx}`} className="flex items-center gap-2 cursor-pointer">
                              <Sparkles className="w-4 h-4 text-pink-600" />
                              <span className="text-sm font-semibold">Dança MBV</span>
                            </Label>
                          </div>
                          {member.link_to_dance && (
                            <div className="ml-6 text-xs text-pink-700">
                              O integrante sera vinculado como lider e membro da equipe de dança com a funcao fixa <strong>Dançar</strong>.
                            </div>
                          )}
                        </div>

                        {/* Recepção */}
                        <div className="bg-white rounded-lg p-3 border border-amber-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Checkbox
                              id={`reception-${idx}`}
                              checked={member.link_to_reception}
                              onCheckedChange={(checked) => {
                                const updated = [...formData.members];
                                updated[idx].link_to_reception = checked;
                                setFormData({ ...formData, members: updated });
                              }}
                            />
                            <Label htmlFor={`reception-${idx}`} className="flex items-center gap-2 cursor-pointer">
                              <Handshake className="w-4 h-4 text-amber-700" />
                              <span className="text-sm font-semibold">Equipe de Recepção</span>
                            </Label>
                          </div>
                          {member.link_to_reception && (
                            <div className="ml-6 space-y-2 text-xs">
                              <Label className="text-xs">Funções:</Label>
                              <div className="flex flex-wrap gap-1">
                                {['Receber', 'Acolher', 'Conectar', 'Servir', 'Orientar', 'Pegar Dados'].map(role => (
                                  <label key={role} className="flex items-center gap-1 px-2 py-1 bg-amber-50 rounded">
                                    <input
                                      type="checkbox"
                                      checked={member.reception_roles?.includes(role)}
                                      onChange={(e) => {
                                        const updated = [...formData.members];
                                        if (e.target.checked) {
                                          updated[idx].reception_roles = [...(member.reception_roles || []), role];
                                        } else {
                                          updated[idx].reception_roles = member.reception_roles.filter(r => r !== role);
                                        }
                                        setFormData({ ...formData, members: updated });
                                      }}
                                      className="w-3 h-3"
                                    />
                                    <span className="text-xs">{role}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Kids MBV */}
                        <div className="bg-white rounded-lg p-3 border border-pink-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Checkbox
                              id={`kids-${idx}`}
                              checked={member.link_to_kids}
                              onCheckedChange={(checked) => {
                                const updated = [...formData.members];
                                updated[idx].link_to_kids = checked;
                                setFormData({ ...formData, members: updated });
                              }}
                            />
                            <Label htmlFor={`kids-${idx}`} className="flex items-center gap-2 cursor-pointer">
                              <Baby className="w-4 h-4 text-pink-600" />
                              <span className="text-sm font-semibold">Kids MBV</span>
                            </Label>
                          </div>
                          {member.link_to_kids && (
                            <div className="ml-6 space-y-2 text-xs">
                              <Label className="text-xs">Funções:</Label>
                              <div className="flex flex-wrap gap-1">
                                {['Professor', 'Ajudante', 'Ministração do Louvor', 'Teatro'].map(role => (
                                  <label key={role} className="flex items-center gap-1 px-2 py-1 bg-pink-50 rounded">
                                    <input
                                      type="checkbox"
                                      checked={member.kids_roles?.includes(role)}
                                      onChange={(e) => {
                                        const updated = [...formData.members];
                                        if (e.target.checked) {
                                          updated[idx].kids_roles = [...(member.kids_roles || []), role];
                                        } else {
                                          updated[idx].kids_roles = member.kids_roles.filter(r => r !== role);
                                        }
                                        setFormData({ ...formData, members: updated });
                                      }}
                                      className="w-3 h-3"
                                    />
                                    <span className="text-xs">{role}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Input
                value={searchMember}
                onChange={(e) => setSearchMember(e.target.value)}
                placeholder="Digite o nome para adicionar membro..."
              />
              {searchMember.length > 2 && members.length > 0 && (
                <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                  {members.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => selectMember(member)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors"
                    >
                      <p className="font-medium text-gray-900">{member.full_name}</p>
                      <p className="text-sm text-gray-500">{member.email}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva as responsabilidades..."
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data de Início</label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                Liderança ativa
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={resetForm} className="flex-1">
                Cancelar
              </Button>
              <Button 
                onClick={handleSubmit} 
                className="flex-1"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingLeader ? 'Atualizar' : 'Adicionar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover este líder? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
